import { CLIENT_COMMAND_OPERATION_PERMISSIONS } from './clientCommandPermissions';

export const CLIENT_COMMAND_ITEM_NAMES = Object.freeze({
  summary: 'ClientCommandSummary',
  workspace: 'ClientsWorkspace',
  commandCenter: 'ClientCommandCenter',
  clientInformation: 'ClientInformation',
  applicationOptions: 'ClientApplicationOptions',
  entraSetupScript: 'ClientEntraSetupScript',
});

export const CLIENT_COMMAND_OPERATIONS = Object.freeze({
  loadClientsWorkspace: 'LoadClientsWorkspace',
  showClientCommandCenter: 'ShowClientCommandCenter',
  loadClientCommandCenter: 'LoadClientCommandCenter',
  runClientReadinessChecks: 'RunClientReadinessChecks',
  loadClientInformation: 'LoadClientInformation',
  saveClientInformation: 'SaveClientInformation',
  setClientApplicationOptions: 'SetClientApplicationOptions',
  generateClientEntraSetupScript: 'GenerateClientEntraSetupScript',
});

export const CLIENT_COMMAND_PERMISSIONS = Object.freeze({
  clientInformation: 'ClientCommandClientInformation',
  saveClientInformation: CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientInformation,
  setClientApplicationOptions: CLIENT_COMMAND_OPERATION_PERMISSIONS.setClientApplicationOptions,
});

export const CLIENT_INFORMATION_EDIT_FIELDS = Object.freeze([
  'Name',
  'DisplayName',
  'EIN',
  'FirstName',
  'LastName',
  'Email',
  'PrimaryPhone',
  'SecondaryPhone',
  'PrimaryAddress',
  'SecondaryAddress',
]);

export const APPLICATION_OPTION_CARD_METADATA = Object.freeze({
  Name: 'ClientApplicationOption',
  DisplayName: 'Application Options',
  Fields: [
    {
      FieldName: 'DisplayName',
      DisplayName: 'Application Option',
      PrimaryField: true,
      ShowOnCard: true,
      GridSequence: 1,
    },
    {
      FieldName: 'Category',
      DisplayName: 'Category',
      ShowOnCard: true,
      GridSequence: 2,
    },
    {
      FieldName: 'Availability',
      DisplayName: 'Availability',
      ShowOnCard: true,
      GridSequence: 3,
    },
    {
      FieldName: 'Key',
      DisplayName: 'Configuration Key',
      ShowOnCard: true,
      GridSequence: 4,
    },
  ],
});

export const unwrapOperationResult = (result) => (
  result?.Data ?? result?.data ?? result ?? null
);

export const getResultItems = (result) => {
  const normalized = unwrapOperationResult(result);
  const items = normalized?.Items ?? normalized?.items;
  if (Array.isArray(items)) return items;
  if (typeof items?.toArray === 'function') return items.toArray();
  if (typeof items?.first === 'function') {
    const first = items.first();
    return first ? [first] : [];
  }
  if (normalized?.Item) return [normalized.Item];
  if (normalized?.item) return [normalized.item];
  return [];
};

export const getFirstResultItem = (result) => getResultItems(result)[0] ?? null;

export const getOperationEventId = (result) => {
  const normalized = unwrapOperationResult(result);
  const firstItem = getFirstResultItem(normalized);
  return [
    normalized?.EventId,
    normalized?.eventId,
    normalized?.CorrelationID,
    normalized?.CorrelationId,
    normalized?.correlationId,
    normalized?.CorrelationEvent?.EventId,
    normalized?.CorrelationEvent?.UniqueId,
    normalized?.CorrelationEvent?._id,
    normalized?.Value?.EventId,
    normalized?.Value?.UniqueId,
    normalized?.Value?._id,
    firstItem?.EventId,
    firstItem?.UniqueId,
    firstItem?._id,
    result?.EventId,
    result?.CorrelationID,
    result?.CorrelationId,
  ].map((value) => String(value ?? '').trim())
    .find((value) => (
      value
      && value !== '00000000-0000-0000-0000-000000000000'
    )) || '';
};

export const isAsyncOperationAccepted = (result) => {
  const normalized = unwrapOperationResult(result);
  const message = normalized?.DisplayMessage
    ?? normalized?.Message?.DisplayMessage
    ?? normalized?.Message?.Content
    ?? normalized?.Messages?.[0]?.DisplayMessage
    ?? normalized?.Messages?.[0]?.Content
    ?? '';
  return Boolean(
    normalized?.EventPersisted === true
    || normalized?.eventPersisted === true
    || getOperationEventId(result)
    || /ACTION_IN_PROGRESS|action (?:is )?in progress/i.test(message),
  );
};

export const getCommandInternalRedirectRoute = (result) => {
  const normalized = unwrapOperationResult(result);
  const route = normalized?.UICommands?.InternalRedirect
    ?? normalized?.uiCommands?.InternalRedirect;
  if (typeof route !== 'string') return null;

  const trimmed = route.trim();
  const path = trimmed.split(/[?#]/, 1)[0];
  if (
    !/^\/command(?:\/|$|\?|#)/i.test(trimmed)
    || trimmed.includes('\\')
    || path.split('/').includes('..')
  ) {
    return null;
  }

  return trimmed;
};

export const getResultMessage = (result, fallback) => {
  const normalized = unwrapOperationResult(result);
  const validationMessage = Object.values(normalized?.ValidationErrors ?? {})
    .map((validationError) => (
      validationError?.DisplayMessage
      ?? validationError?.Content
      ?? validationError?.Message?.DisplayMessage
      ?? validationError?.Message?.Content
    ))
    .filter(Boolean)
    .join('\n');
  return validationMessage || (normalized?.DisplayMessage
    ?? normalized?.Message?.DisplayMessage
    ?? normalized?.Message?.Content
    ?? normalized?.Messages?.[0]?.DisplayMessage
    ?? normalized?.Messages?.[0]?.Content
    ?? fallback);
};

export const isSuccessfulResult = (result) => {
  const normalized = unwrapOperationResult(result);
  return normalized?.Success !== false && getResultItems(normalized).length > 0;
};

export const getFirstMetadata = (result, manager) => (
  getFirstResultItem(result)
  ?? (typeof manager?.uiMetadata?.first === 'function'
    ? manager.uiMetadata.first()
    : Array.isArray(manager?.uiMetadata)
      ? manager.uiMetadata[0]
      : manager?.uiMetadata)
  ?? null
);

export const selectClientInformationFormFields = (metadata) => {
  const fields = metadata?.Fields ?? metadata?.fields ?? [];
  const allowed = new Set(
    CLIENT_INFORMATION_EDIT_FIELDS.map((field) => field.toLowerCase()),
  );
  return fields.filter((field) => allowed.has(
    String(field?.FieldName ?? field?.fieldName ?? '').toLowerCase(),
  ));
};

export const normalizeSelectedOptionKeys = (keys) => (
  [...new Set((Array.isArray(keys) ? keys : [])
    .map((key) => String(key ?? '').trim())
    .filter(Boolean))]
);
